import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Download, Trash2, FileImage, FileText, Eye, ChevronLeft, ChevronRight, X, Upload, Package } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { TableState } from '@/components/DataState';

const API_BASE = window.location.origin;

const KB = 1024;
const MB = 1048576;

function getStatusClass(status) {
  if (status === 'processed') return 'bg-[hsl(var(--success))]/20 text-[hsl(var(--success))]';
  if (status === 'error') return 'bg-destructive/20 text-destructive';
  return 'bg-secondary text-muted-foreground';
}

function formatSize(bytes) {
  if (!bytes) return 'N/A';
  if (bytes < KB) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / KB).toFixed(1)} KB`;
  return `${(bytes / MB).toFixed(1)} MB`;
}

function formatSender(sender, senderName) {
  if (!sender) return 'N/A';
  if (sender.startsWith('upload:')) {
    const parts = sender.split(':');
    const name = parts[1] || '';
    const email = parts[2] || '';
    return `Upload — ${name}${email ? ` (${email})` : ''}`;
  }
  if (sender === 'manual_upload') return 'Upload';
  const phone = sender.replace('whatsapp:', '');
  if (senderName) return `${senderName} ${phone}`;
  return phone;
}

export default function FileManagerPage() {
  const [searchParams] = useSearchParams();
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sortDir, setSortDir] = useState('desc');

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, sort_dir: sortDir });
      if (search) params.set('search', search);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data } = await api.get(`/files?${params}`);
      setFiles(data.files);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { void 0; }
    setLoading(false);
  }, [page, search, dateFrom, dateTo, sortDir]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  // Auto-open preview from URL query param — runs once after first file load
  const autoPreviewTriggered = React.useRef(false);
  useEffect(() => {
    if (loading || autoPreviewTriggered.current) return;
    const previewId = searchParams.get('preview');
    if (!previewId) return;
    autoPreviewTriggered.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/files/${previewId}/info`);
        if (data && data.file_id) {
          const response = await api.get(`/files/${previewId}/preview`, { responseType: 'blob' });
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const url = window.URL.createObjectURL(blob);
          // Wait for 2 animation frames to ensure DOM is fully painted before opening dialog
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setPreviewFile(data);
              setPreviewUrl(url);
            });
          });
        }
      } catch (err) {
        void 0;
      }
    })();
  }, [loading, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelect = (fileId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === files.length) setSelected(new Set());
    else setSelected(new Set(files.map(f => f.file_id)));
  };

  const handleDownload = async (fileId) => {
    try {
      const response = await api.get(`/files/${fileId}/download`, { responseType: 'blob' });
      const disposition = response.headers['content-disposition'];
      let filename = 'download';
      if (disposition) {
        const match = disposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleBatchDownload = async () => {
    if (selected.size === 0) return;
    try {
      const response = await api.post('/files/batch-download', { file_ids: Array.from(selected) }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'zipai_files.zip';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Batch download started');
    } catch {
      toast.error('Batch download failed');
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file? Associated receipt data will also be removed.')) return;
    try {
      await api.delete(`/files/${fileId}`);
      toast.success('File deleted');
      setSelected(prev => { const next = new Set(prev); next.delete(fileId); return next; });
      fetchFiles();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleBatchDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} files? Associated receipt data will also be removed.`)) return;
    try {
      await api.post('/files/batch-delete', { file_ids: Array.from(selected) });
      toast.success(`${selected.size} files deleted`);
      setSelected(new Set());
      fetchFiles();
    } catch {
      toast.error('Batch delete failed');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.files_extracted) {
        toast.success(`ZIP extracted: ${data.files_extracted} files uploaded & processing`);
      } else {
        toast.success('File uploaded & processing started');
      }
      fetchFiles();
    } catch {
      toast.error('Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handlePreviewFile = async (f) => {
    setPreviewFile(f);
    setPreviewUrl(null);
    try {
      const response = await api.get(`/files/${f.file_id}/preview`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch {
      setPreviewUrl(null);
    }
  };

  const closeFilePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewFile(null);
  };

  const toggleSort = () => {
    setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    setPage(1);
  };

  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return <FileText className="w-5 h-5 text-red-400" />;
    return <FileImage className="w-5 h-5 text-blue-400" />;
  };

  return (
    <div className="space-y-6" data-testid="file-manager-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>File Manager</h1>
          <p className="text-muted-foreground text-sm mt-1">{total} files stored</p>
        </div>
        <div className="flex gap-2">
          <label className="cursor-pointer" data-testid="upload-btn-wrapper">
            <input type="file" className="hidden" accept="image/*,.pdf,.zip,application/zip" onChange={handleUpload} data-testid="upload-input" />
            <Button variant="outline" className="rounded-[4px] pointer-events-none" disabled={uploading} data-testid="upload-btn">
              <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </label>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={(e) => { e.preventDefault(); setPage(1); fetchFiles(); }} className="flex flex-wrap gap-3" data-testid="files-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search files..." className="pl-10 h-10 rounded-[4px]" data-testid="files-search-input" />
        </div>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[160px] h-10 rounded-[4px]" data-testid="files-date-from" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[160px] h-10 rounded-[4px]" data-testid="files-date-to" />
        <Button type="submit" variant="outline" className="h-10 rounded-[4px]" data-testid="files-filter-btn">Filter</Button>
        {(search || dateFrom || dateTo) && (
          <Button type="button" variant="ghost" className="h-10" onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setPage(1); }} data-testid="files-clear-filter">
            <X className="w-4 h-4" />
          </Button>
        )}
      </form>

      {/* Batch Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-secondary/50 border border-border rounded-md px-4 py-2" data-testid="batch-actions">
          <span className="text-sm text-muted-foreground">{selected.size} selected</span>
          <Button size="sm" variant="outline" onClick={handleBatchDownload} className="rounded-[4px]" data-testid="batch-download-btn">
            <Package className="w-3.5 h-3.5 mr-1.5" /> Download ZIP
          </Button>
          <Button size="sm" variant="outline" className="rounded-[4px] text-destructive border-destructive/30 hover:bg-destructive/10" onClick={handleBatchDelete} data-testid="batch-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
          </Button>
        </div>
      )}

      {/* File List */}
      <div className="border border-border rounded-md overflow-hidden" data-testid="files-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="w-10 px-4 py-3">
                  <Checkbox checked={files.length > 0 && selected.size === files.length} onCheckedChange={selectAll} data-testid="select-all-checkbox" />
                </th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">File</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden md:table-cell">Type</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden md:table-cell">Size</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden lg:table-cell">Sender</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Status</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden sm:table-cell cursor-pointer select-none hover:text-foreground transition-colors" onClick={toggleSort} data-testid="sort-date-col">
                  Date {sortDir === 'desc' ? '↓' : '↑'}
                </th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <TableState loading={loading} empty={files.length === 0} colSpan={8} emptyText="No files yet">
                {files.map((f) => (
                  <tr key={f.file_id} className="hover:bg-secondary/30 transition-colors" data-testid={`file-row-${f.file_id}`}>
                    <td className="px-4 py-3">
                      <Checkbox checked={selected.has(f.file_id)} onCheckedChange={() => toggleSelect(f.file_id)} data-testid={`file-checkbox-${f.file_id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {getFileIcon(f.media_type)}
                        <span className="text-sm font-medium truncate max-w-[200px]">{f.original_name || f.filename}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell font-mono">{f.media_type?.split('/')[1]?.toUpperCase() || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell font-mono">{formatSize(f.file_size)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell">
                      {formatSender(f.sender, f.sender_name)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${getStatusClass(f.status)}`}>
                        {f.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell font-mono">
                      {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => handlePreviewFile(f)} data-testid={`preview-file-${f.file_id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => handleDownload(f.file_id)} data-testid={`download-file-${f.file_id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-destructive hover:text-destructive" onClick={() => handleDelete(f.file_id)} data-testid={`delete-file-${f.file_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </TableState>
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid="files-pagination">
            <span className="text-xs text-muted-foreground">Page {page} of {pages}</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="w-7 h-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="files-prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="w-7 h-7" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="files-next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={(open) => !open && closeFilePreview()}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]" data-testid="file-preview-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {previewFile?.original_name || 'Preview'}
            </DialogTitle>
          </DialogHeader>
          {previewFile && previewUrl ? (
            <div className="flex justify-center py-2">
              <img
                src={previewUrl}
                alt="Preview"
                className="max-h-[600px] w-auto object-contain rounded"
              />
            </div>
          ) : previewFile && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
